import type { GuideChatProduct } from './types';

// Every widget text shown word for word, in one place for the business review; the backend owns the answer texts.
export const CHAT_LABELS = {
  headerTitle: 'Assistant IA Réassurez-moi',
  headerSubtitle: 'Assurance de prêt immobilier, réponses fondées sur nos contenus',
  disclaimer: "Informations générales fournies par l'IA, à vérifier : nos conseillers sont là pour ça.",
  disclaimerNoAdviser: "Informations générales fournies par l'IA, à vérifier.",
  emptyState: "Posez votre question sur l'assurance de prêt immobilier : je réponds simplement, à partir de notre guide.",
  resumeNotice: 'Reprenons où nous en étions.',
  loading: 'Je cherche dans le guide…',
  loadingAria: "L'assistant rédige une réponse",
  error: "Je n'ai pas pu obtenir de réponse. Réessayez dans un instant.",
  inputLabel: 'Votre question',
  inputPlaceholder: "Votre question sur l'assurance de prêt…",
  inputPlaceholderAnswer: 'Votre réponse…',
  inputPlaceholderClosed: 'Conversation terminée',
  send: 'Envoyer',
  newConversation: 'Nouvelle conversation',
  conversationClosed: 'Cette conversation est terminée. Vous pouvez en démarrer une nouvelle à tout moment.',
  launcher: 'Une question ?',
  launcherOpen: "Ouvrir l'assistant IA Réassurez-moi",
  launcherClose: "Fermer l'assistant IA Réassurez-moi",
  closePanel: "Fermer l'assistant",
  closePanelTitle: 'Fermer (Échap)',
  visitorPrefix: 'Vous : ',
  assistantPrefix: 'Assistant : ',
  quickRepliesLabel: 'Réponses rapides',
  suggestionsLabel: 'Suggestions de questions',
  sources: 'Sources',
  subjectRedirectLink: 'Voir le parcours dédié',
  quoteCtaDefaultLabel: 'Comparer les offres',
  feedbackGroup: 'Cette réponse vous a-t-elle aidé ?',
  feedbackQuestion: 'Utile ?',
  feedbackYes: 'Oui',
  feedbackNo: 'Non',
  feedbackThanks: 'Merci !',
  unavailable: "L'assistant est momentanément indisponible : faites-vous rappeler par un expert.",
  unavailableRetry: "L'assistant est momentanément indisponible, réessayez dans un instant.",
  unavailableIntro: 'En attendant, vous pouvez :',
  unavailableGuideLink: 'consulter le guide assurance emprunteur',
  unavailableAppointmentLink: 'demander un rappel ou lancer une simulation',
  prefillButton: 'Continuer sur le formulaire',
  prefillHint: 'Vos réponses sont reportées, modifiables à tout moment',
  prefillOfferButton: 'Ouvrir le formulaire de comparaison',
  prefillOfferHint: 'Vos devis vous sont envoyés par e-mail à la fin de la comparaison',
  phoneCtaAria: 'Appeler un conseiller au',
  openerAsk: 'Poser ma question',
  openerRegion: "Suggestion de l'assistant IA Réassurez-moi",
  openerClose: 'Fermer la suggestion',
  openerCloseTitle: 'Fermer',
  // Mirrors the backend form-exit opener, used when the backend cannot compose one.
  formExitOpener: 'Vous devez partir ? Vous pourrez finir votre devis plus tard, ou être rappelé par un de nos conseillers pour le compléter ensemble.',
  formExitResumeSuggestion: 'Comment reprendre mon devis plus tard ?',
  formExitCallbackSuggestion: 'Être rappelé par un conseiller',
  // %s is the product label the backend sends with the answer.
  productSwitched: 'Je vous réponds à partir du guide %s.',
} as const;

interface ProductLabels {
  // Shown when the backend cannot be reached at all: no hand-over form can be sent then, so the other products promise no callback.
  unavailable: string;
  headerSubtitle: string;
  emptyState: string;
  inputPlaceholder: string;
  unavailableGuideLink: string;
  unavailableAppointmentLink: string;
}

// The loan entries repeat CHAT_LABELS on purpose: the loan wording was validated, the other products derive from it.
export const PRODUCT_LABELS: Record<GuideChatProduct, ProductLabels> = {
  loan: {
    unavailable: CHAT_LABELS.unavailable,
    headerSubtitle: CHAT_LABELS.headerSubtitle,
    emptyState: CHAT_LABELS.emptyState,
    inputPlaceholder: CHAT_LABELS.inputPlaceholder,
    unavailableGuideLink: CHAT_LABELS.unavailableGuideLink,
    unavailableAppointmentLink: CHAT_LABELS.unavailableAppointmentLink,
  },
  health: {
    unavailable: CHAT_LABELS.unavailableRetry,
    headerSubtitle: 'Mutuelle santé, réponses fondées sur nos contenus',
    emptyState: 'Posez votre question sur la mutuelle santé : je réponds simplement, à partir de notre guide.',
    inputPlaceholder: 'Votre question sur la mutuelle santé…',
    unavailableGuideLink: 'consulter le guide mutuelle santé',
    unavailableAppointmentLink: 'comparer les mutuelles santé',
  },
  travel: {
    unavailable: CHAT_LABELS.unavailableRetry,
    headerSubtitle: 'Assurance voyage, réponses fondées sur nos contenus',
    emptyState: "Posez votre question sur l'assurance voyage : je réponds simplement, à partir de notre guide.",
    inputPlaceholder: "Votre question sur l'assurance voyage…",
    unavailableGuideLink: 'consulter le guide assurance voyage',
    unavailableAppointmentLink: 'comparer les assurances voyage',
  },
  provident: {
    unavailable: CHAT_LABELS.unavailableRetry,
    headerSubtitle: 'Prévoyance, réponses fondées sur nos contenus',
    emptyState: 'Posez votre question sur la prévoyance (décès, obsèques, dépendance) : je réponds simplement, à partir de notre guide.',
    inputPlaceholder: 'Votre question sur la prévoyance…',
    unavailableGuideLink: 'consulter le guide prévoyance',
    unavailableAppointmentLink: 'découvrir les offres prévoyance',
  },
};

export function labelsFor(product?: GuideChatProduct | null): ProductLabels {
  return PRODUCT_LABELS[product ?? 'loan'] ?? PRODUCT_LABELS.loan;
}
